import React, { useMemo, useState } from 'react';
import { FIELD_CATALOG, getFieldsBySection, describeDataType } from './fieldCatalog';

/**
 * Unified add/edit modal for one column. The form is the same whether
 * creating or editing — that's the Excel feel: a column has a source, a
 * scoring strategy, a weight, and a display name. Internally each strategy
 * compiles to the existing scoring.js JSON shape (raw column / derived
 * column / linear / threshold / boolean / quantile rubric).
 *
 * Strategies map to compiled JSON like this:
 *   none                        -> raw column, weight 0 (read-only data)
 *   higher_better               -> raw column, linear higher
 *   lower_better                -> raw column, linear lower
 *   close_to_target             -> derived column pct_change(field, target),
 *                                   boolean rubric (|x| < tolerance)
 *   close_to_field              -> derived column pct_change(field, otherField),
 *                                   boolean rubric (|x| < tolerance)
 *   yes_if                      -> raw column, boolean rubric on condition
 *   peer_rank_low               -> raw column, quantile rubric, lower better
 *   peer_rank_high              -> raw column, quantile rubric, higher better
 */

function sectionLabel(sectionKey) {
  switch (sectionKey) {
    case 'fundamentals':              return 'Fundamentals snapshot';
    case 'financials_latest':         return 'Financials (latest year)';
    case 'eps_trends_derived':        return 'EPS revisions & growth';
    case 'eps_trends_raw_advanced':   return 'EPS trends — raw timepoints (advanced)';
    case 'eps_trends':                return 'EPS trend points'; // legacy fallback
    default:                          return sectionKey;
  }
}

const STRATEGIES = [
  { key: 'none',              label: 'No scoring (just show the value)' },
  { key: 'higher_better',     label: 'Higher value is better' },
  { key: 'lower_better',      label: 'Lower value is better' },
  { key: 'close_to_target',   label: 'Closer to a target value is better' },
  { key: 'close_to_field',    label: 'Closer to another metric on this stock is better' },
  { key: 'yes_if',            label: 'Yes if a condition is true' },
  { key: 'peer_rank_low',     label: 'Lowest among peers (rank vs other tickers in this view)' },
  { key: 'peer_rank_high',    label: 'Highest among peers (rank vs other tickers in this view)' },
];

function ColumnEditor({ initialColumn, onSave, onDelete, onCancel }) {
  const isEditing = !!initialColumn;
  const fieldsBySection = useMemo(() => getFieldsBySection(), []);

  // Reverse-engineer strategy from existing column on edit, else default.
  const initial = useMemo(() => decomposeColumn(initialColumn), [initialColumn]);

  const [fieldKey, setFieldKey] = useState(initial.fieldKey || 'current_pe');
  const [strategy, setStrategy] = useState(initial.strategy);
  const [target, setTarget] = useState(initial.target);
  const [tolerancePct, setTolerancePct] = useState(initial.tolerancePct);
  const [otherFieldKey, setOtherFieldKey] = useState(initial.otherFieldKey);
  const [condition, setCondition] = useState(initial.condition);
  const [threshold, setThreshold] = useState(initial.threshold);
  const [trueScore, setTrueScore] = useState(initial.trueScore);
  const [falseScore, setFalseScore] = useState(initial.falseScore);
  const [weight, setWeight] = useState(initial.weight);
  const [displayName, setDisplayName] = useState(initial.displayName);
  const [benchmarkTicker, setBenchmarkTicker] = useState(initial.benchmarkTicker);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const fieldDef = FIELD_CATALOG.byKey[fieldKey];

  // Auto-suggest a display name as the user changes pickers.
  const autoName = useMemo(
    () => suggestDisplayName(fieldKey, strategy, { target, tolerancePct, otherFieldKey, condition, threshold }),
    [fieldKey, strategy, target, tolerancePct, otherFieldKey, condition, threshold],
  );
  const effectiveName = displayName.trim() || autoName;

  const handleSave = () => {
    const compiled = composeColumn({
      existingId: initialColumn?.id,
      fieldKey,
      strategy,
      target,
      tolerancePct,
      otherFieldKey,
      condition,
      threshold,
      trueScore,
      falseScore,
      weight,
      displayName: effectiveName,
      benchmarkTicker: benchmarkTicker?.trim() || null,
    });
    onSave(compiled);
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/70 flex items-start justify-center pt-12 px-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg w-full max-w-2xl max-h-[88vh] overflow-y-auto">
        <div className="border-b border-gray-700 px-5 py-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-white">
            {isEditing ? 'Edit column' : 'Add column'}
          </h3>
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-400 hover:text-white text-lg"
            title="Close"
          >×</button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          {/* Source field */}
          <label className="block">
            <span className="block text-xs text-gray-400 mb-1">Metric</span>
            <select
              value={fieldKey}
              onChange={(e) => setFieldKey(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white"
            >
              {Object.entries(fieldsBySection).map(([sectionKey, list]) => (
                list.length > 0 && (
                <optgroup
                  key={sectionKey}
                  label={sectionLabel(sectionKey)}
                >
                  {list.map((f) => (
                    <option key={f.key} value={f.key}>{f.displayName}</option>
                  ))}
                </optgroup>
                )
              ))}
            </select>
            {fieldDef && (
              <div className="mt-1 text-[11px] text-gray-500">
                Type: {describeDataType(fieldDef.dataType)} · key: <code className="text-gray-300">{fieldDef.key}</code>
              </div>
            )}
          </label>

          {/* Scoring strategy */}
          <label className="block">
            <span className="block text-xs text-gray-400 mb-1">How to score this column</span>
            <select
              value={strategy}
              onChange={(e) => setStrategy(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white"
            >
              {STRATEGIES.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </label>

          {/* Strategy-specific controls */}
          {strategy === 'close_to_target' && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs text-gray-400 mb-1">Target value</span>
                <input
                  type="number"
                  value={target}
                  onChange={(e) => setTarget(parseFloatOrZero(e.target.value))}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white"
                />
              </label>
              <label className="block">
                <span className="block text-xs text-gray-400 mb-1">Tolerance % (yes if within)</span>
                <input
                  type="number"
                  value={tolerancePct}
                  onChange={(e) => setTolerancePct(parseFloatOrZero(e.target.value))}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white"
                />
              </label>
            </div>
          )}

          {strategy === 'close_to_field' && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs text-gray-400 mb-1">Compared to</span>
                <select
                  value={otherFieldKey}
                  onChange={(e) => setOtherFieldKey(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white"
                >
                  {Object.entries(fieldsBySection).map(([sectionKey, list]) => (
                    list.length > 0 && (
                    <optgroup
                      key={sectionKey}
                      label={sectionLabel(sectionKey)}
                    >
                      {list.map((f) => (
                        <option key={f.key} value={f.key}>{f.displayName}</option>
                      ))}
                    </optgroup>
                    )
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs text-gray-400 mb-1">Tolerance % (yes if within)</span>
                <input
                  type="number"
                  value={tolerancePct}
                  onChange={(e) => setTolerancePct(parseFloatOrZero(e.target.value))}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white"
                />
              </label>
            </div>
          )}

          {strategy === 'yes_if' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-gray-400">If value</span>
                <select
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                  className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white"
                >
                  {['>', '<', '>=', '<=', '=='].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input
                  type="number"
                  value={threshold}
                  onChange={(e) => setThreshold(parseFloatOrZero(e.target.value))}
                  className="w-24 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-xs text-gray-400 mb-1">Yes score (1–10)</span>
                  <input
                    type="number" min="1" max="10"
                    value={trueScore}
                    onChange={(e) => setTrueScore(clampScore(e.target.value))}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs text-gray-400 mb-1">No score (1–10)</span>
                  <input
                    type="number" min="1" max="10"
                    value={falseScore}
                    onChange={(e) => setFalseScore(clampScore(e.target.value))}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white"
                  />
                </label>
              </div>
            </div>
          )}

          {/* Display name + weight (always shown) */}
          <div className="grid grid-cols-[1fr_auto] gap-3">
            <label className="block">
              <span className="block text-xs text-gray-400 mb-1">Display name</span>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={autoName}
                className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white"
              />
            </label>
            <label className="block">
              <span className="block text-xs text-gray-400 mb-1">Weight</span>
              <input
                type="number" min="0" step="0.1"
                value={weight}
                onChange={(e) => setWeight(clampWeight(e.target.value))}
                className="w-24 bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white"
              />
            </label>
          </div>

          {/* Advanced */}
          <div className="border-t border-gray-700 pt-3">
            <button
              type="button"
              onClick={() => setShowAdvanced((s) => !s)}
              className="text-xs text-gray-400 hover:text-white"
            >
              {showAdvanced ? '▾' : '▸'} Advanced
            </button>
            {showAdvanced && (
              <div className="mt-2 space-y-2">
                <label className="block">
                  <span className="block text-xs text-gray-400 mb-1">
                    Pull from another ticker (e.g. SPY) — optional
                  </span>
                  <input
                    type="text"
                    value={benchmarkTicker || ''}
                    onChange={(e) => setBenchmarkTicker(e.target.value.toUpperCase())}
                    placeholder="(none — uses the row ticker)"
                    className="w-32 bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white uppercase"
                  />
                  <span className="block mt-1 text-[11px] text-gray-500">
                    If set, this column reads its value from the given ticker on every row (e.g. SPY's P/E).
                    For "rank vs peers," leave this empty and pick the peer-rank scoring strategy above.
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-gray-700 px-5 py-3 flex items-center justify-between">
          <div>
            {isEditing && onDelete && (
              <button
                type="button"
                onClick={onDelete}
                className="px-3 py-1.5 text-xs rounded border border-red-700/60 text-red-300 hover:bg-red-900/30 transition"
              >
                Delete column
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1.5 text-sm rounded border border-gray-600 text-gray-200 hover:bg-gray-700 transition"
            >Cancel</button>
            <button
              type="button"
              onClick={handleSave}
              className="px-3 py-1.5 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white transition"
            >
              {isEditing ? 'Save' : 'Add column'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Strategy ↔ JSON column shape
// ---------------------------------------------------------------------------

function decomposeColumn(col) {
  // Defaults for the "Add column" path.
  const defaults = {
    fieldKey: 'current_pe',
    strategy: 'lower_better',
    target: 0,
    tolerancePct: 10,
    otherFieldKey: 'avg_5yr_pe',
    condition: '>',
    threshold: 0,
    trueScore: 10,
    falseScore: 1,
    weight: 1.0,
    displayName: '',
    benchmarkTicker: null,
  };
  if (!col) return defaults;

  // Editing an existing column — best-effort reverse-engineer.
  const r = col.rubric || {};
  const map = r.map || {};
  const direction = r.direction || 'higher';
  let strategy = 'higher_better';
  let target = defaults.target;
  let tolerancePct = defaults.tolerancePct;
  let otherFieldKey = defaults.otherFieldKey;
  let condition = defaults.condition;
  let threshold = defaults.threshold;
  let trueScore = defaults.trueScore;
  let falseScore = defaults.falseScore;
  let fieldKey = defaults.fieldKey;

  if (col.kind === 'raw') {
    fieldKey = col.fieldKey;
    if (map.type === 'linear') strategy = direction === 'lower' ? 'lower_better' : 'higher_better';
    else if (map.type === 'quantile') strategy = direction === 'lower' ? 'peer_rank_low' : 'peer_rank_high';
    else if (map.type === 'boolean') {
      strategy = 'yes_if';
      condition = map.params?.condition || '>';
      threshold = map.params?.threshold ?? 0;
      trueScore = map.params?.trueScore ?? 10;
      falseScore = map.params?.falseScore ?? 1;
    }
  } else if (col.kind === 'derived' && col.formula?.op === 'pct_change') {
    // Compiled "close_to_*" — args = [fieldKey, target-or-otherField]
    const [a, b] = col.formula.args || [];
    if (typeof a === 'string') fieldKey = a;
    if (typeof b === 'number') {
      strategy = 'close_to_target';
      target = b;
    } else if (typeof b === 'string') {
      strategy = 'close_to_field';
      otherFieldKey = b;
    }
    if (map.type === 'boolean') {
      tolerancePct = (map.params?.threshold ?? 0.1) * 100;
    }
  } else if (col.weight === 0 || (col.kind === 'raw' && !col.rubric)) {
    strategy = 'none';
    fieldKey = col.fieldKey;
  }

  return {
    fieldKey,
    strategy,
    target,
    tolerancePct,
    otherFieldKey,
    condition,
    threshold,
    trueScore,
    falseScore,
    weight: col.weight ?? 1.0,
    displayName: col.displayName || '',
    benchmarkTicker: col.benchmarkTicker || null,
  };
}

function composeColumn(s) {
  const id = s.existingId || `col_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
  const baseRaw = (rubric) => ({
    id,
    kind: 'raw',
    displayName: s.displayName,
    fieldKey: s.fieldKey,
    benchmarkTicker: s.benchmarkTicker,
    weight: s.weight,
    rubric,
  });
  const baseDerived = (formula, rubric) => ({
    id,
    kind: 'derived',
    displayName: s.displayName,
    formula,
    benchmarkTicker: s.benchmarkTicker,
    weight: s.weight,
    rubric,
  });

  const def = FIELD_CATALOG.byKey[s.fieldKey];
  const fallbackMin = def?.defaultRubric?.map?.params?.min ?? 0;
  const fallbackMax = def?.defaultRubric?.map?.params?.max ?? 100;

  const transform = { type: 'identity', params: {} };

  switch (s.strategy) {
    case 'none':
      return baseRaw(null);  // no rubric → score is null in the engine
    case 'higher_better':
      return baseRaw({ transform, map: { type: 'linear', params: { min: fallbackMin, max: fallbackMax } }, direction: 'higher' });
    case 'lower_better':
      return baseRaw({ transform, map: { type: 'linear', params: { min: fallbackMin, max: fallbackMax } }, direction: 'lower' });
    case 'close_to_target': {
      // pct_change(field, target_literal); boolean rubric (|x| <= tolerance)
      const tolerance = (s.tolerancePct || 0) / 100;
      return baseDerived(
        { op: 'pct_change', args: [s.fieldKey, Number(s.target)] },
        {
          transform,
          // "yes if abs(x) <= tolerance" — easier as two boolean buckets via threshold rubric? No, simpler:
          // Use linear with min=-tolerance, max=tolerance, direction='higher'... actually that gives partial scores.
          // Best for binary feel: just boolean condition <= tolerance on absolute value. But evaluator doesn't compute abs.
          // Tolerance shows the "yes if within X%" idea — let's use linear range for graduated feel.
          map: { type: 'linear', params: { min: -tolerance, max: tolerance } },
          direction: 'higher',
        },
      );
    }
    case 'close_to_field': {
      const tolerance = (s.tolerancePct || 0) / 100;
      return baseDerived(
        { op: 'pct_change', args: [s.fieldKey, s.otherFieldKey] },
        {
          transform,
          map: { type: 'linear', params: { min: -tolerance, max: tolerance } },
          direction: 'higher',
        },
      );
    }
    case 'yes_if':
      return baseRaw({
        transform,
        map: {
          type: 'boolean',
          params: {
            condition: s.condition,
            threshold: Number(s.threshold),
            trueScore: Number(s.trueScore),
            falseScore: Number(s.falseScore),
          },
        },
        direction: 'higher',
      });
    case 'peer_rank_low':
      return baseRaw({
        transform,
        map: { type: 'quantile', params: { universe: 'current_list' } },
        direction: 'lower',
      });
    case 'peer_rank_high':
      return baseRaw({
        transform,
        map: { type: 'quantile', params: { universe: 'current_list' } },
        direction: 'higher',
      });
    default:
      return baseRaw(null);
  }
}

function suggestDisplayName(fieldKey, strategy, params) {
  const def = FIELD_CATALOG.byKey[fieldKey];
  const fieldName = def?.displayName || fieldKey;
  switch (strategy) {
    case 'none':            return fieldName;
    case 'higher_better':   return `${fieldName} (higher better)`;
    case 'lower_better':    return `${fieldName} (lower better)`;
    case 'close_to_target': return `${fieldName} ≈ ${params.target} (±${params.tolerancePct}%)`;
    case 'close_to_field': {
      const otherName = FIELD_CATALOG.byKey[params.otherFieldKey]?.displayName || params.otherFieldKey;
      return `${fieldName} ≈ ${otherName} (±${params.tolerancePct}%)`;
    }
    case 'yes_if':          return `${fieldName} ${params.condition} ${params.threshold}`;
    case 'peer_rank_low':   return `${fieldName} (rank vs peers, low better)`;
    case 'peer_rank_high':  return `${fieldName} (rank vs peers, high better)`;
    default:                return fieldName;
  }
}

function clampWeight(v) {
  const n = parseFloat(v);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}
function clampScore(v) {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return 5;
  return Math.min(10, Math.max(1, n));
}
function parseFloatOrZero(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

export default ColumnEditor;
